/* eslint-disable camelcase */
import React, {useState} from 'react';
import {Checkbox, Grid, Stack, Typography, useTheme} from '@mui/material';
import {useSnackbar} from 'notistack';

import {
  createAuditLog,
  getPageData,
  getSearchResultsData
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import Card from '@mui/material/Card';
import {APP} from 'src/styles/variables';
import {
  CardDiv,
  MainWrapperDiv,
  InfoDiv,
  SpaceSpan,
  LabelTypography
} from './CardComponentStyles';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';

import HelperUtils from '@shared/utils/HelperUtils';
import EncryptUtils from 'src/utils/EncryptUtils';
import FormConstants from '@shared/constants/FormConstants';
import AuthUtils from 'src/utils/AuthUtils';
import {useModuleContext} from '@context/ModuleContext';
import {useAppContext} from '@context/AppContext';
import {PulseLoader} from 'react-spinners';
import {rest} from 'lodash';
import {useParams} from 'react-router-dom';
import {generateSubModuleIdentifier} from 'src/utils/helperUtils';
import {
  BASE_DATA_GRID_ROW,
  LOCAL_SEARCH,
  PARAMS_KEY,
  SEARCH_CONTEXT
} from 'src/routes/BaseRoute';
import HighlighterWrapper from '@components/highlighterWrapper/HighlighterWrapper';
interface CardComponentProps {
  fields: any;
  data: any;
  columns_per_row: number;
  // eslint-disable-next-line react/boolean-prop-naming
  is_border_required?: boolean;
  // eslint-disable-next-line react/boolean-prop-naming
  is_space_needed_between_label_value?: boolean;
  // eslint-disable-next-line react/boolean-prop-naming
  is_colon_needed?: boolean;
  module?: any;
  sub_module?: any;
  sectionUIData?: any;
  selectedRowDetails?: any;
  is_bold_font?: any;
  component_type?: any;
  recordId?: string;
  procedure_type?: string;
  accession_number?: string;
  currentTab?: any;
  tabPermission?: any;
  specimen_number?: any;
  specimen_number_id?: any;
  procedure_id?: any;
  demographic_id?: any;
  loading?: any;
}

const CardComponent = (props: CardComponentProps): React.ReactElement => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const params = useParams();
  const theme = useTheme();
  const {
    fields,
    data,
    component_type,
    // is_border_required = true,
    columns_per_row,
    is_space_needed_between_label_value = false,
    is_colon_needed = true,
    module,
    sub_module,
    sectionUIData,
    selectedRowDetails,
    // is_bold_font = true,
    recordId,
    currentTab,
    procedure_type,
    accession_number,
    specimen_number,
    specimen_number_id,
    procedure_id,
    demographic_id
  } = props;
  const {enqueueSnackbar} = useSnackbar();
  const [cardData, setCardData]: any = React.useState({});
  const [loader, setLoader] = useState(false);
  const {moduleState} = useModuleContext();
  const {isBH, isBHReason}: any = moduleState;
  const [tabViewPermission, setTabViewPermission] = useState<boolean>(
    () =>
      sectionUIData?.sql_data?.TabpermissiontoView === true &&
      AuthUtils.getPermissions().some(
        (permission: any) =>
          permission.function === 'View Orientation/Gender Info'
      )
  );

  const localSearch = EncryptUtils.getDecryptedData(
    location?.search,
    LOCAL_SEARCH
  );

  const search_context = EncryptUtils.getDecryptedData(
    location?.search,
    SEARCH_CONTEXT
  );

  const parsedLocalSearch = localSearch ? JSON.parse(localSearch) : {};

  const baseDataGridRow = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_DATA_GRID_ROW
  );
  let visitDetails: any;
  if (baseDataGridRow) {
    visitDetails = JSON.parse(baseDataGridRow);
  }

  React.useEffect(() => {
    if (
      currentTab?.[`@${selectedRowDetails?.id}@`] ===
        FormConstants.DATA_GRID_TYPES?.SEXUAL_ORIENTATION_TAB &&
      sectionUIData?.sql_data?.TabpermissiontoView === true
    ) {
      const hasPermission = AuthUtils.getPermissions()?.some(
        (permission: any) =>
          permission?.function === 'View Orientation/Gender Info'
      );
      if (hasPermission) {
        setTabViewPermission(true);
      }
    }
  }, [currentTab]);

  const init = async () => {
    try {
      if (
        module &&
        sub_module &&
        sectionUIData?.sql_data?.sql &&
        sectionUIData?.sql_data?.variables
      ) {
        setLoader(true);
        let payload: any = {
          sql_data: sectionUIData?.sql_data,
          user_id: selectedRowDetails?.row?.EmpNo,
          record_id: selectedRowDetails?.id || recordId || '',
          OrderID: selectedRowDetails?.row?.OrderID,
          OrderName: selectedRowDetails?.row?.OrderName,
          OrderDate: selectedRowDetails?.row?.OrderDate,
          legacyApp: selectedRowDetails?.row?.legacyApp,
          legacyMRN: selectedRowDetails?.row?.legacyMRN || recordId || '',
          item_vendor_id: selectedRowDetails?.row?.ItemVendorID,
          transaction_id: selectedRowDetails?.row?.TransactionID,
          invoice_id: selectedRowDetails?.row?.InvoiceID,
          employment_number: selectedRowDetails?.row?.EmpNo,
          pay_date: selectedRowDetails?.row?.PayDate,
          distribution_order: selectedRowDetails?.row?.DistributionOrder,
          is_active: selectedRowDetails?.row?.IsActive,
          priority: selectedRowDetails?.row?.Priority,
          is_dependent: selectedRowDetails?.row?.IsDependent,
          EffectDt: selectedRowDetails?.row?.EffectDt,
          audit_sub_module_identifier: generateSubModuleIdentifier(
            module,
            sub_module,
            {
              specimen_number_id,
              procedure_id,
              procedure_type,
              demographic_id,
              row_id: selectedRowDetails?.row?.ID,
              patient_visit_id:
                selectedRowDetails?.row?.PatientVisitId ||
                visitDetails?.PatientVisitID,
              ID: selectedRowDetails?.row?.ID || recordId,
              sub_module_identifier_id:
                sectionUIData?.sql_data?.sub_module_identifier_id
            }
          ),
          patient_info_id: selectedRowDetails?.row?.PatientInformationID,
          prescription_no: selectedRowDetails?.row?.PrescriptionNumber,
          drug: selectedRowDetails?.row?.Drug,
          refill_no: selectedRowDetails?.row?.RefillNumber,
          audit_identifier: AuthUtils.getAuditIdentifier(),
          audit_identifier_type: AuthUtils.getAuditIdentifierType(),
          audit_legacy_app: AuthUtils.getAuditLegacyApp(),
          isBH_selected: sectionUIData?.sql_data?.ignoreIsBH ? false : isBH,
          isBH_reason: isBHReason,
          application_name: applicationName,
          procedure_type: procedure_type,
          accession_number: accession_number,
          specimen_number: specimen_number,
          specimen_number_id: specimen_number_id,
          patient_id: recordId,
          patient_visit_id:
            selectedRowDetails?.PatientVisitID || visitDetails?.PatientVisitID,
          LegacyMRN: selectedRowDetails?.row?.LegacyMRN
        };
        if (parsedLocalSearch?.is_local_search && parsedLocalSearch?.ID) {
          const params = new URLSearchParams(window.location.search);
          const encrypted = params.get(PARAMS_KEY);
          const {local_search} = EncryptUtils.getDecryptedParams(encrypted);
          const latest_parsedLocalSearch = local_search?.length
            ? JSON.parse(local_search)
            : null;

          if (latest_parsedLocalSearch?.nested_ID) {
            payload.local_search_ID = latest_parsedLocalSearch?.nested_ID;
          }
          // payload.record_id = parsedLocalSearch?.ID,
          // payload.local_search = parsedLocalSearch?.is_local_search;
          // payload.local_search_ID = parsedLocalSearch?.ID;
          // payload.local_search_isBhdatafilter =
          //   parsedLocalSearch?.ISBhdataFilter;
          if (
            module === FormConstants.MODULE.LIS &&
            sub_module.toLowerCase() === FormConstants.SUB_MODULE.RESULTS
          ) {
            payload.specimen_number_id = parsedLocalSearch?.ID;
          }
          if (
            sectionUIData.name?.toLowerCase() ===
            parsedLocalSearch?.Level2?.toLowerCase()
          ) {
            payload.local_search_ID = parsedLocalSearch?.Level2ID;
          }
        }
        if (
          currentTab?.[`@${selectedRowDetails?.id}@`] ===
          FormConstants.DATA_GRID_TYPES?.SEXUAL_ORIENTATION_TAB
        ) {
          payload = {
            ...payload,
            tabPermission: tabViewPermission
          } as typeof payload & {tabPermission: any};
        }
        if (
          props?.module === 'patient-accounting' &&
          props?.sub_module === 'invoice' &&
          selectedRowDetails?.row?.IsRCMTransaction
        ) {
          payload.IsRCMTransaction = true;
        }
        if (
          module === FormConstants.MODULE.LIS &&
          sub_module.toLowerCase() === FormConstants.SUB_MODULE.PRODUCTINFO
        ) {
          payload.procedure_id = procedure_id;
        }
        const encryptRequestData = EncryptUtils.encryptedData(payload);
        const res = await getPageData(applicationName, module, sub_module, {
          tk: encryptRequestData
        });
        setCardData(res?.data);
        setLoader(false);
      } else if (module === 'audit_logs') {
        setLoader(true);
        const payload = {
          sql_data: sectionUIData?.sql_data,
          user_id: selectedRowDetails?.row?.EmpNo,
          record_id:
            selectedRowDetails?.row?.Row_ID ||
            selectedRowDetails?.id ||
            recordId ||
            '',
          AuditType: selectedRowDetails?.row?.Audit_Type,
          ModuleSelected: selectedRowDetails?.row?.Module_Name,
          OrderID: selectedRowDetails?.row?.OrderID,
          OrderName: selectedRowDetails?.row?.OrderName,
          OrderDate: selectedRowDetails?.row?.OrderDate,
          legacyApp: selectedRowDetails?.row?.legacyApp,
          legacyMRN: selectedRowDetails?.row?.legacyMRN || recordId || '',
          item_vendor_id: selectedRowDetails?.row?.ItemVendorID,
          transaction_id: selectedRowDetails?.row?.TransactionID,
          invoice_id: selectedRowDetails?.row?.InvoiceID,
          employment_number: selectedRowDetails?.row?.EmpNo,
          pay_date: selectedRowDetails?.row?.PayDate,
          distribution_order: selectedRowDetails?.row?.DistributionOrder,
          is_active: selectedRowDetails?.row?.IsActive,
          priority: selectedRowDetails?.row?.Priority,
          is_dependent: selectedRowDetails?.row?.IsDependent,
          EffectDt: selectedRowDetails?.row?.EffectDt,
          audit_sub_module_identifier: generateSubModuleIdentifier(
            module,
            sub_module,
            {
              specimen_number_id,
              procedure_id,
              procedure_type,
              demographic_id,
              ID: selectedRowDetails?.row?.ID,
              sub_module_identifier_id:
                sectionUIData?.sql_data?.sub_module_identifier_id
            }
          ),
          patient_info_id: selectedRowDetails?.row?.PatientInformationID,
          prescription_no: selectedRowDetails?.row?.PrescriptionNumber,
          drug: selectedRowDetails?.row?.Drug,
          refill_no: selectedRowDetails?.row?.RefillNumber,
          audit_identifier: AuthUtils.getAuditIdentifier(),
          audit_identifier_type: AuthUtils.getAuditIdentifierType(),
          audit_legacy_app: AuthUtils.getAuditLegacyApp(),
          isBH_selected: sectionUIData?.sql_data?.ignoreIsBH ? false : isBH,
          isBH_reason: isBHReason,
          application_name: applicationName,
          procedure_type: procedure_type,
          accession_number: accession_number,
          specimen_number: specimen_number,
          specimen_number_id: specimen_number_id
        };
        const encryptRequestData = EncryptUtils.encryptedData(payload);
        const res = await getSearchResultsData(applicationName, module, {
          tk: encryptRequestData
        });
        setCardData(res?.data);
        setLoader(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLoader(false);
    }
  };

  const logCardData = (dataObj: any) => {
    let rowId: any = '';

    const acceptedList = ['ID', 'PatientID', 'LegacyMRN', 'OrderID'];
    let uniqueKey = '';

    for (const key in dataObj) {
      if (Object.prototype.hasOwnProperty.call(dataObj, key)) {
        for (const acceptedKey of acceptedList) {
          if (dataObj[acceptedKey]) {
            if (!uniqueKey?.length) {
              uniqueKey = acceptedKey;
            }
            if (!rowId) {
              rowId = dataObj[uniqueKey];
            }
            break; // Stop iterating after finding the first accepted key
          }
        }
      }
    }

    let eventDescription = params.module_name
      ? params.module_name + ' -> '
      : '';

    if (params.submodule_name) {
      eventDescription += `${params.submodule_name}`;
    }

    if (sectionUIData?.sql_data?.audit_event_description) {
      eventDescription += ` -> ${sectionUIData?.sql_data?.audit_event_description}`;
    } else if (sectionUIData?.name) {
      eventDescription += ` -> ${sectionUIData?.name}`;
    }

    const data = {
      module: params.module_name,
      sub_module: params.submodule_name,
      sub_module_identifier: JSON.stringify({
        uniqueKey,
        rowId
      }),
      procedure_type,
      application_name: applicationName,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      event_description: `Card Results : ${eventDescription}`,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(props.module || params.module_name, data);
  };

  React.useEffect(() => {
    if (cardData?.length) {
      logCardData(cardData?.[0]);
    }
  }, [cardData]);

  React.useEffect(() => {
    init();
    setCardData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionUIData?.sql_data?.sql]);

  React.useEffect(() => {
    if (
      sub_module === FormConstants.PAGE_SECTION_TYPES.DOCUMENTS ||
      props?.component_type === FormConstants.COMPONENT_TYPE.DOCUMENT ||
      sub_module === FormConstants.RCM_PATIENT_ACCOUNTING.PAYMENT.AUTO_POST
    ) {
      setCardData(data);
    }
    // init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function findMaxLengthOfLine(content: string): number {
    // Splitting the string into lines
    const lines = content.split('\n');

    // Finding the maximum length
    let maxLength = 0;
    lines.forEach(line => {
      if (line.length > maxLength) {
        maxLength = line.length;
      }
    });
    return maxLength;
  }

  const renderCardComponent = () => {
    let DocStringMaxLength = '';
    if (
      sectionUIData?.card_type === FormConstants.DATA_GRID_TYPES.CARD_REPORT &&
      cardData[0]?.[sectionUIData?.fields?.[0]?.data_column]
    ) {
      DocStringMaxLength = findMaxLengthOfLine(
        cardData[0]?.[sectionUIData?.fields?.[0]?.data_column]
      ).toString();
    }
    return (
      <Card
        sx={{
          mt: 1,
          boxShadow: 5,
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          width: sectionUIData?.width || '100%',
          minHeight: '100px',
          scrollBehavior: 'smooth',
          overflowX: sectionUIData?.displayHorizontalScroll ? 'auto' : 'hidden'
        }}>
        {!tabViewPermission &&
        currentTab?.[`@${selectedRowDetails?.id}@`] ===
          FormConstants.DATA_GRID_TYPES?.SEXUAL_ORIENTATION_TAB ? (
          <CardDiv
            // isBorderRequired={true}
            component_type={''}>
            <div style={{color: 'red', textAlign: 'center'}}>
              Permission denied for this user !
            </div>{' '}
          </CardDiv>
        ) : (
          <CardDiv
            {...rest}
            // isBorderRequired={!loader && is_border_required}
            component_type={component_type}>
            {sectionUIData?.label?.length ? (
              <Typography
                color="primary"
                sx={{
                  fontWeight: 'bold',
                  marginLeft: '0.7rem',
                  color: `${theme.palette.common.black}`,
                  fontSize: '1rem !important',
                  alignItems: 'flex-start'
                }}>
                {sectionUIData?.label}
              </Typography>
            ) : null}
            {loader || props?.loading ? (
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  paddingBottom: 5
                }}>
                <PulseLoader size={10} color={theme.palette.primary.main} />
              </Stack>
            ) : (
              <Grid
                sx={{width: '100%', padding: '0', margin: 1}}
                container
                spacing={1.8}>
                {fields?.map?.((mainItem: any, mainIndex: number) => {
                  if (mainItem?.is_section_header) {
                    return (
                      <Grid
                        sx={{
                          padding: '0.45em 0.4em 0.1em !important'
                        }}
                        item
                        md={12}
                        xs={12}
                        key={mainIndex}>
                        <Typography
                          sx={{
                            color: APP.PALETTE.CARD_COLOR.LABEL,
                            fontSize: '0.95rem',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase'
                          }}>
                          {mainItem?.label}
                        </Typography>
                      </Grid>
                    );
                  }

                  return (
                    <Grid
                      sx={{
                        padding: '0.2em 0.4em !important'
                      }}
                      item
                      md={12 / columns_per_row}
                      xs={12}
                      key={mainIndex}>
                      <InfoDiv
                        style={DocStringMaxLength ? {justifyContent: ''} : {}}>
                        <div
                          style={
                            mainItem?.displayfull
                              ? {
                                  display: 'flex',
                                  flexWrap: 'nowrap',
                                  minWidth: `${columns_per_row * 100}%`
                                }
                              : {}
                          }>
                          <div
                            style={{
                              display: 'flex',
                              // justifyContent: '',
                              // alignItems: 'center',
                              minWidth: DocStringMaxLength
                                ? `${DocStringMaxLength}px`
                                : 'auto'
                            }}>
                            <LabelTypography
                              sx={{
                                flex: '0 0 auto',
                                whiteSpace: 'nowrap',
                                margin: '0 5px 0 0',
                                color: APP.PALETTE.CARD_COLOR.LABEL,
                                fontWeight: 'normal'
                              }}>
                              {mainItem?.label}
                              {cardData?.[0]?.[mainItem?.data_column] !==
                                'No Data Found.' && is_colon_needed
                                ? '  :    '
                                : ''}
                            </LabelTypography>
                            <Typography>
                              {is_space_needed_between_label_value ? (
                                <SpaceSpan />
                              ) : (
                                ''
                              )}
                            </Typography>
                            {mainItem.is_pre_formatter_required ? (
                              // <pre
                              //   style={{
                              //     margin: '0',
                              //     color: APP.PALETTE.CARDS.VALUE,
                              //     fontWeight: 'bold',
                              //     whiteSpace: DocStringMaxLength
                              //       ? 'pre-wrap'
                              //       : 'pre-line',
                              //   }}>
                              //   {cardData?.[0]?.[mainItem?.data_column]}
                              // </pre>
                              <Stack
                                sx={{
                                  display: 'flex',
                                  overflow: 'hidden'
                                }}>
                                <Typography
                                  sx={{
                                    whiteSpace: DocStringMaxLength
                                      ? 'pre-wrap'
                                      : 'pre-line',
                                    fontFamily: DocStringMaxLength
                                      ? 'monospace'
                                      : 'Public Sans,sans-serif',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: `${
                                      cardData?.[0]?.[mainItem?.data_column] ||
                                      ''
                                    }`
                                  }}
                                />
                              </Stack>
                            ) : (
                              <Typography
                                sx={{
                                  margin: '0',
                                  color: APP.PALETTE.CARD_COLOR.TEXT,
                                  fontWeight: 'bold',
                                  wordWrap: 'break-word'
                                }}>
                                {mainItem?.type === 'boolean' &&
                                mainItem?.checkbox ? (
                                  <Checkbox
                                    size="small"
                                    sx={{
                                      padding: 0
                                    }}
                                    checked={
                                      !!cardData?.[0]?.[mainItem?.data_column]
                                    }
                                  />
                                ) : mainItem?.type === 'boolean' &&
                                  cardData?.[0]?.[mainItem?.data_column] ===
                                    null ? (
                                  ''
                                ) : (
                                  HelperUtils.formatData(
                                    cardData?.[0]?.[mainItem?.data_column],
                                    mainItem?.type,
                                    mainItem?.formatter || mainItem?.format
                                  ) || '-'
                                )}
                              </Typography>
                            )}
                          </div>
                        </div>
                      </InfoDiv>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardDiv>
        )}
      </Card>
    );
  };

  return (
    <MainWrapperDiv overflow="visible" style={{marginBottom: '8px'}}>
      <HighlighterWrapper keywords={[search_context]}>
        {renderCardComponent()}
      </HighlighterWrapper>
    </MainWrapperDiv>
  );
};

export default CardComponent;
